import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, AlertCircle, List, Lock } from 'lucide-react';
import { ref, push, set, onValue, remove, update } from 'firebase/database';
import { database } from '../../firebase/config';
import { useIsMobile } from '../../hooks/useIsMobile';
import SetlistCard from './SetlistCard';
import SetlistModals from './SetlistModals';

export default function SetlistTab({ user, bandId, isPremium }) {
  const [setlists, setSetlists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [bandName, setBandName] = useState('');
  const [expandedSetlist, setExpandedSetlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSetlist, setEditingSetlist] = useState(null);
  const [showSongPicker, setShowSongPicker] = useState(false);
  const [pickerSetlistId, setPickerSetlistId] = useState(null);

  // Playback state
  const [playingSetlistId, setPlayingSetlistId] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentDuration, setCurrentDuration] = useState(0);
  const audioRef = useRef(null);
  const gapTimeoutRef = useRef(null);

  const isMobile = useIsMobile();

  // ── Data listeners ────────────────────────────────────────
  useEffect(() => {
    if (!user || !bandId) { setSetlists([]); return; }
    const setlistsRef = ref(database, `bands/${bandId}/setlists`);
    const unsubscribe = onValue(setlistsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map(key => ({
          id: key, ...data[key],
          items: data[key].items
            ? Object.keys(data[key].items).map(itemKey => ({ id: itemKey, ...data[key].items[itemKey] }))
                .sort((a, b) => a.order - b.order)
            : []
        }));
        arr.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
        setSetlists(arr);
      } else { setSetlists([]); }
    });
    return () => unsubscribe();
  }, [user, bandId]);

  useEffect(() => {
    if (!user || !bandId) { setSongs([]); return; }
    const songsRef = ref(database, `bands/${bandId}/songs`);
    const unsubscribe = onValue(songsRef, (snapshot) => {
      const data = snapshot.val();
      setSongs(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : []);
    });
    return () => unsubscribe();
  }, [user, bandId]);

  // Band name (for MediaSession artist field)
  useEffect(() => {
    if (!bandId) { setBandName(''); return; }
    const nameRef = ref(database, `bands/${bandId}/name`);
    const unsub = onValue(nameRef, snap => setBandName(snap.val() || ''));
    return () => unsub();
  }, [bandId]);

  // ── Helpers ───────────────────────────────────────────────
  const getSongById = useCallback((songId) => songs.find(s => s.id === songId), [songs]);

  const getVersionById = (song, versionId) => {
    if (!song?.versions) return null;
    return song.versions[versionId] ? { id: versionId, ...song.versions[versionId] } : null;
  };

  const getSortedVersions = (song) => {
    if (!song?.versions) return [];
    return Object.keys(song.versions)
      .map(key => ({ id: key, ...song.versions[key] }))
      .filter(v => v.fileURL)
      .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
  };

  const getAvailableSongs = () => songs.filter(song => getSortedVersions(song).length > 0);

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateTotalDuration = (items, continuousMode = false) => {
    if (!items || items.length === 0) return 0;
    let total = 0;
    items.forEach((item, idx) => {
      total += item.cachedDuration || 0;
      if (!continuousMode && idx < items.length - 1) total += item.gapAfterSeconds || 30;
    });
    return total;
  };

  const clearGapTimeout = () => {
    if (gapTimeoutRef.current) {
      clearTimeout(gapTimeoutRef.current);
      gapTimeoutRef.current = null;
    }
  };

  // ── Playback ──────────────────────────────────────────────
  const handleTrackEnd = useCallback(() => {
    const setlist = setlists.find(s => s.id === playingSetlistId);
    if (!setlist) return;

    const isLast = currentItemIndex >= setlist.items.length - 1;
    if (isLast) {
      setIsPlaying(false);
      setPlayingSetlistId(null);
      setCurrentItemIndex(0);
      return;
    }

    const continuousMode = !!setlist.continuousMode;
    const currentItem = setlist.items[currentItemIndex];
    const gapSeconds = continuousMode ? 0 : (currentItem?.gapAfterSeconds ?? 30);

    if (gapSeconds === 0) {
      // Synchronous transition: keep the user-gesture chain alive so iOS / lock
      // screen / Bluetooth allow the next track to autoplay reliably.
      const nextItem = setlist.items[currentItemIndex + 1];
      const nextSong = songs.find(s => s.id === nextItem.songId);
      const nextUrl = nextSong?.versions?.[nextItem.versionId]?.fileURL;

      if (nextUrl && audioRef.current) {
        audioRef.current.src = nextUrl;
        const p = audioRef.current.play();
        if (p !== undefined) p.catch(err => {
          if (err.name !== 'AbortError') console.error('Auto-next playback error:', err);
        });
      }
      setCurrentItemIndex(prev => prev + 1);
    } else {
      // Gap mode: setTimeout (less reliable in background; warn user in UI)
      clearGapTimeout();
      gapTimeoutRef.current = setTimeout(() => {
        gapTimeoutRef.current = null;
        setCurrentItemIndex(prev => prev + 1);
      }, gapSeconds * 1000);
    }
  }, [playingSetlistId, currentItemIndex, setlists, songs]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setCurrentDuration(audio.duration);
    const onEnded = () => handleTrackEnd();
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [playingSetlistId, currentItemIndex, handleTrackEnd]);

  const getCurrentAudioUrl = useCallback(() => {
    const setlist = setlists.find(s => s.id === playingSetlistId);
    if (!setlist || !setlist.items[currentItemIndex]) return null;
    const item = setlist.items[currentItemIndex];
    const song = songs.find(s => s.id === item.songId);
    if (!song?.versions) return null;
    return song.versions[item.versionId]?.fileURL || null;
  }, [playingSetlistId, currentItemIndex, setlists, songs]);

  useEffect(() => {
    const audio = audioRef.current;
    const url = getCurrentAudioUrl();
    if (!audio || !url) return;
    if (audio.src !== url) audio.src = url;
    if (isPlaying) {
      const p = audio.play();
      if (p !== undefined) p.catch(err => { if (err.name !== 'AbortError') console.error('Playback error:', err); });
    }
  }, [getCurrentAudioUrl, currentItemIndex, isPlaying]);

  // ── MediaSession (lock screen, Bluetooth, CarPlay) ────────
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!playingSetlistId) {
      navigator.mediaSession.metadata = null;
      try { navigator.mediaSession.playbackState = 'none'; } catch (e) {}
      return;
    }
    const setlist = setlists.find(s => s.id === playingSetlistId);
    const item = setlist?.items[currentItemIndex];
    const song = songs.find(s => s.id === item?.songId);
    if (!song || !setlist) return;

    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: song.name,
        artist: bandName || 'Tapsody',
        album: setlist.name,
      });
    } catch (e) { /* MediaMetadata may not exist on older Safari */ }
  }, [playingSetlistId, currentItemIndex, setlists, songs, bandName]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !playingSetlistId) return;
    try {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (e) {}
  }, [isPlaying, playingSetlistId]);

  // Update position state continuously so lock-screen scrubber works
  useEffect(() => {
    if (!('mediaSession' in navigator) || !playingSetlistId) return;
    if (!currentDuration || !isFinite(currentDuration)) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: currentDuration,
        playbackRate: audioRef.current?.playbackRate || 1,
        position: Math.min(currentTime, currentDuration),
      });
    } catch (e) { /* iOS sometimes throws on rapid updates */ }
  }, [currentTime, currentDuration, playingSetlistId]);

  // ── Player actions (used by UI + MediaSession handlers) ───
  const seekTo = useCallback((seconds) => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration || 0;
    audioRef.current.currentTime = Math.max(0, Math.min(dur, seconds));
  }, []);

  const skipBackward = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  }, []);

  const skipForward = useCallback(() => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration || 0;
    audioRef.current.currentTime = Math.min(dur, audioRef.current.currentTime + 10);
  }, []);

  const skipToNext = useCallback(() => {
    const setlist = setlists.find(s => s.id === playingSetlistId);
    if (!setlist) return;
    clearGapTimeout();
    if (currentItemIndex < setlist.items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    }
  }, [setlists, playingSetlistId, currentItemIndex]);

  const skipToPrevious = useCallback(() => {
    if (!audioRef.current) return;
    clearGapTimeout();
    // Standard music-player convention: restart current if >3s in, else go to previous
    if (audioRef.current.currentTime > 3 || currentItemIndex === 0) {
      audioRef.current.currentTime = 0;
    } else {
      setCurrentItemIndex(prev => prev - 1);
    }
  }, [currentItemIndex]);

  // ── MediaSession action handlers ──────────────────────────
  useEffect(() => {
    if (!('mediaSession' in navigator) || !playingSetlistId) return;
    const ms = navigator.mediaSession;
    const audio = audioRef.current;

    ms.setActionHandler('play', () => {
      if (!audio) return;
      const p = audio.play();
      if (p !== undefined) p.catch(() => {});
    });
    ms.setActionHandler('pause', () => audio?.pause());
    ms.setActionHandler('previoustrack', skipToPrevious);
    ms.setActionHandler('nexttrack', skipToNext);
    try {
      ms.setActionHandler('seekbackward', (details) => {
        if (!audio) return;
        const offset = details.seekOffset || 10;
        audio.currentTime = Math.max(0, audio.currentTime - offset);
      });
      ms.setActionHandler('seekforward', (details) => {
        if (!audio) return;
        const offset = details.seekOffset || 10;
        audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + offset);
      });
      ms.setActionHandler('seekto', (details) => {
        if (!audio || details.seekTime == null) return;
        audio.currentTime = details.seekTime;
      });
    } catch (e) { /* not supported on all browsers */ }

    return () => {
      try {
        ms.setActionHandler('play', null);
        ms.setActionHandler('pause', null);
        ms.setActionHandler('previoustrack', null);
        ms.setActionHandler('nexttrack', null);
        ms.setActionHandler('seekbackward', null);
        ms.setActionHandler('seekforward', null);
        ms.setActionHandler('seekto', null);
      } catch (e) {}
    };
  }, [playingSetlistId, skipToPrevious, skipToNext]);

  const startPlayback = (setlistId) => {
    const setlist = setlists.find(s => s.id === setlistId);
    if (!setlist || setlist.items.length === 0) return;
    setPlayingSetlistId(setlistId); setCurrentItemIndex(0); setIsPlaying(true);
  };

  const jumpToSong = (setlistId, idx) => {
    const setlist = setlists.find(s => s.id === setlistId);
    if (!setlist || !setlist.items[idx]) return;
    // Tap on the currently playing song while playing → no-op (avoid surprise restart)
    if (playingSetlistId === setlistId && currentItemIndex === idx && isPlaying) return;
    clearGapTimeout();
    setPlayingSetlistId(setlistId);
    setCurrentItemIndex(idx);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); }
    else {
      const p = audioRef.current.play();
      if (p !== undefined) p.catch(err => { if (err.name !== 'AbortError') console.error('Playback error:', err); });
    }
  };

  const stopPlayback = () => {
    clearGapTimeout();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlayingSetlistId(null); setIsPlaying(false); setCurrentItemIndex(0);
  };

  // Cleanup gap timeout on unmount
  useEffect(() => () => clearGapTimeout(), []);

  // ── CRUD ──────────────────────────────────────────────────
  const createSetlist = async (name) => {
    try {
      setLoading(true); setError('');
      const setlistsRef = ref(database, `bands/${bandId}/setlists`);
      const newRef = push(setlistsRef);
      await set(newRef, {
        name: name.trim(),
        continuousMode: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user.uid,
        createdByName: user.displayName || user.email
      });
      setShowCreateModal(false);
      setExpandedSetlist(newRef.key);
    } catch (err) { setError('Failed to create setlist: ' + err.message); }
    finally { setLoading(false); }
  };

  const updateSetlistName = async (setlistId, name) => {
    try {
      setLoading(true);
      await update(ref(database, `bands/${bandId}/setlists/${setlistId}`), {
        name: name.trim(), updatedAt: new Date().toISOString()
      });
      setEditingSetlist(null);
    } catch (err) { setError('Failed to update setlist: ' + err.message); }
    finally { setLoading(false); }
  };

  const toggleContinuousMode = async (setlistId) => {
    const setlist = setlists.find(s => s.id === setlistId);
    if (!setlist) return;
    try {
      await update(ref(database, `bands/${bandId}/setlists/${setlistId}`), {
        continuousMode: !setlist.continuousMode,
        updatedAt: new Date().toISOString()
      });
    } catch (err) { setError('Failed to update continuous mode: ' + err.message); }
  };

  const deleteSetlist = async (setlistId) => {
    if (!window.confirm('Delete this setlist?')) return;
    try {
      await remove(ref(database, `bands/${bandId}/setlists/${setlistId}`));
      if (playingSetlistId === setlistId) { setPlayingSetlistId(null); setIsPlaying(false); }
    } catch (err) { setError('Failed to delete setlist: ' + err.message); }
  };

  const addSongToSetlist = async (setlistId, songId, versionId) => {
    const song = getSongById(songId);
    const version = getVersionById(song, versionId);
    if (!version) return;
    try {
      setLoading(true);
      const setlist = setlists.find(s => s.id === setlistId);
      const newOrder = (setlist?.items || []).length;
      let duration = 0;
      if (version.fileURL) {
        try {
          const audio = new Audio();
          audio.src = version.fileURL;
          await new Promise((resolve) => {
            audio.onloadedmetadata = () => { duration = audio.duration; resolve(); };
            audio.onerror = () => resolve();
            setTimeout(resolve, 5000);
          });
        } catch (e) { console.warn('Could not get duration:', e); }
      }
      const itemsRef = ref(database, `bands/${bandId}/setlists/${setlistId}/items`);
      const newItemRef = push(itemsRef);
      await set(newItemRef, {
        songId, versionId, order: newOrder, gapAfterSeconds: 30,
        cachedDuration: duration, addedAt: new Date().toISOString(), addedBy: user.uid
      });
      await update(ref(database, `bands/${bandId}/setlists/${setlistId}`), { updatedAt: new Date().toISOString() });
      setShowSongPicker(false); setPickerSetlistId(null);
    } catch (err) { setError('Failed to add song: ' + err.message); }
    finally { setLoading(false); }
  };

  const removeItemFromSetlist = async (setlistId, itemId) => {
    try {
      await remove(ref(database, `bands/${bandId}/setlists/${setlistId}/items/${itemId}`));
      await update(ref(database, `bands/${bandId}/setlists/${setlistId}`), { updatedAt: new Date().toISOString() });
    } catch (err) { setError('Failed to remove song: ' + err.message); }
  };

  const updateGapTime = async (setlistId, itemId, gapSeconds) => {
    try {
      await update(ref(database, `bands/${bandId}/setlists/${setlistId}/items/${itemId}`), {
        gapAfterSeconds: parseInt(gapSeconds) || 0
      });
    } catch (err) { setError('Failed to update gap time: ' + err.message); }
  };

  const reorderItems = async (setlistId, items) => {
    const updates = {};
    items.forEach((item, idx) => {
      updates[`bands/${bandId}/setlists/${setlistId}/items/${item.id}/order`] = idx;
    });
    try { await update(ref(database), updates); }
    catch (err) { console.error('Failed to reorder:', err); }
  };

  // ── Premium gate ──────────────────────────────────────────
  if (!isPremium) {
    return (
      <div className="space-y-6">
        <div className="card-3d rounded-card p-8 text-center">
          <div className="bg-brand/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-brand" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Setlist Feature</h2>
          <p className="text-text-subtle mb-6 max-w-md mx-auto">
            Create setlists to practice your songs in order. Add songs, adjust timing, and play through your entire set.
          </p>
          <div className="border border-surface-border-light rounded-card-btn p-4 mb-6 max-w-sm mx-auto">
            <p className="text-sm text-text-muted">
              <span className="text-brand font-semibold">Premium Feature</span> — Upgrade to unlock setlists and practice your gigs like a pro.
            </p>
          </div>
          <button
            disabled
            className="bg-brand/50 cursor-not-allowed px-8 py-3 font-semibold text-white/70 rounded-pill"
          >
            Coming Soon
          </button>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <audio ref={audioRef} preload="auto" />

      {error && (
        <div className="bg-red-900/50 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <List className="w-6 h-6 text-brand" />
            Setlists
          </h1>
          <p className="text-text-subtle text-sm">Create and practice your setlists</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold flex items-center gap-2 rounded-pill"
        >
          <Plus className="w-4 h-4" />
          New Setlist
        </button>
      </div>

      {/* Setlists */}
      {setlists.length === 0 ? (
        <div className="card-3d rounded-card p-12 text-center">
          <List className="w-12 h-12 text-text-subtle opacity-50 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No setlists yet</h3>
          <p className="text-text-subtle mb-4">Create your first setlist to start practicing</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand hover:bg-brand-hover transition px-6 py-2 font-semibold rounded-pill"
          >
            Create Setlist
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {setlists.map(setlist => (
            <SetlistCard
              key={setlist.id}
              setlist={setlist}
              isExpanded={expandedSetlist === setlist.id}
              onToggleExpand={() => setExpandedSetlist(expandedSetlist === setlist.id ? null : setlist.id)}
              // Playback
              isCurrentlyPlaying={playingSetlistId === setlist.id}
              isPlaying={isPlaying}
              currentItemIndex={currentItemIndex}
              currentTime={currentTime}
              currentDuration={currentDuration}
              onStartPlayback={() => startPlayback(setlist.id)}
              onJumpToSong={(idx) => jumpToSong(setlist.id, idx)}
              onTogglePlayPause={togglePlayPause}
              onSkipToNext={skipToNext}
              onSkipToPrevious={skipToPrevious}
              onSkipBackward={skipBackward}
              onSkipForward={skipForward}
              onSeek={seekTo}
              onStopPlayback={stopPlayback}
              onToggleContinuousMode={() => toggleContinuousMode(setlist.id)}
              // Actions
              onDelete={() => deleteSetlist(setlist.id)}
              onEdit={() => { setEditingSetlist(setlist.id); }}
              onAddSong={() => { setPickerSetlistId(setlist.id); setShowSongPicker(true); }}
              onRemoveItem={(itemId) => removeItemFromSetlist(setlist.id, itemId)}
              onUpdateGapTime={(itemId, gap) => updateGapTime(setlist.id, itemId, gap)}
              onReorderItems={(reordered) => reorderItems(setlist.id, reordered)}
              // Helpers
              getSongById={getSongById}
              formatDuration={formatDuration}
              calculateTotalDuration={calculateTotalDuration}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <SetlistModals
        showCreateModal={showCreateModal}
        onCloseCreate={() => setShowCreateModal(false)}
        onCreateSetlist={createSetlist}
        editingSetlist={editingSetlist}
        editingSetlistName={setlists.find(s => s.id === editingSetlist)?.name || ''}
        onCloseEdit={() => setEditingSetlist(null)}
        onUpdateName={(name) => updateSetlistName(editingSetlist, name)}
        showSongPicker={showSongPicker}
        pickerSetlistId={pickerSetlistId}
        onClosePicker={() => { setShowSongPicker(false); setPickerSetlistId(null); }}
        onAddSongToSetlist={addSongToSetlist}
        availableSongs={getAvailableSongs()}
        getSortedVersions={getSortedVersions}
        loading={loading}
      />
    </div>
  );
}
